import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import dojoLogoIconLight from "../assets/dojotrack_logo_icon_png.png";
import dojoLogoIconDark from "../assets/dojotrack_logo_icon_png_light.png";

export default function InactiveStudioSidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    setCollapsed(true);
  }, []);

  if (!user) {
    return null;
  }

  const navigation = [
    {
      name: "Settings",
      href: "/app/settings/billing",
      icon: Cog6ToothIcon,
    },
  ];

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      {/* Toggle Button (visible on 2xl and below) */}
      <button
        className="fixed top-4 left-4 z-50 2xl:hidden bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2 shadow"
        onClick={() => setCollapsed((prev) => !prev)}
        aria-label={collapsed ? "Open sidebar" : "Close sidebar"}
      >
        {collapsed ? (
          <Bars3Icon className="h-6 w-6 text-gray-900 dark:text-gray-100" />
        ) : (
          <XMarkIcon className="h-6 w-6 text-gray-900 dark:text-gray-100" />
        )}
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed z-40 top-0 left-0 h-full bg-gray-50 dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700
          transition-transform duration-200
          w-64
          ${collapsed ? "-translate-x-full" : "translate-x-0"}
          2xl:translate-x-0 2xl:static 2xl:block
        `}
        style={{ minHeight: "100vh" }}
      >
        <div className="flex flex-col w-64 h-screen min-h-0 bg-gray-50 dark:bg-gray-800">
          <div className="hidden 2xl:block flex-col items-start justify-center border-b border-gray-200 dark:border-gray-700 px-4 pt-4 pb-4 flex-shrink-0">
            {/* Show correct logo icon for light/dark mode */}
            <img
              src={dojoLogoIconLight}
              alt="DojoTrack Logo Icon"
              className="h-6 w-auto block dark:hidden"
            />
            <img
              src={dojoLogoIconDark}
              alt="DojoTrack Logo Icon"
              className="h-6 w-auto hidden dark:block"
            />
          </div>

          {/* Subscription Notice */}
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <p className="font-medium">Subscription Required</p>
              <p className="mt-1">Your studio subscription is inactive. Please update your billing below to reactivate your account and regain access to all features.</p>
            </div>
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto space-y-1 px-2 py-2 2xl:pt-2 pt-16">
            {navigation.map((item) => {
              const isActive = location.pathname.startsWith('/app/settings');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center rounded-md px-3 py-2 text-sm font-medium ${
                    isActive
                      ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400"
                      : "text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400"
                  }`}
                  onClick={() => setCollapsed(true)}
                >
                  <item.icon
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive
                        ? "text-indigo-600 dark:text-indigo-400"
                        : "text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
                    }`}
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {user && (
            <div
              className="border-t border-gray-200 dark:border-gray-700 p-4 flex-shrink-0"
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
              <button
                onClick={handleLogout}
                className="group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-500 transition"
              >
                <ArrowRightOnRectangleIcon
                  className="mr-3 h-6 w-6 flex-shrink-0 text-red-500 dark:text-red-400"
                  aria-hidden="true"
                />
                Logout
              </button>
              <div className="mt-4 text-xs text-gray-400 dark:text-gray-500 text-center select-none">
                © {new Date().getFullYear()} DojoTrack, LLC
              </div>
              <div className="h-4"></div>
            </div>
          )}
        </div>
      </div>

      {/* Overlay for collapsed sidebar on small screens */}
      {!collapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-30 2xl:hidden"
          onClick={() => setCollapsed(true)}
        />
      )}
    </>
  );
}
